import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";

import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { stockFilterStatusOptions } from "../../shared/utility";
import type { EmptyModalState } from "../../components/modals/EmptyModal";
import type { SlideOverState } from "../../components/slideOver/SlideOver";
import type { SelectOption } from "../../types/api";
import type { ProductFilters, ProductsResponse } from "../../types/products";

export function ProductsViewModel(logout: () => void) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<ProductsResponse>({
    current_page: 1, data: [], last_page: 1, per_page: 20, total: 0,
  });

  const [primaryCategories, setPrimaryCategories] = useState<SelectOption[]>([]);
  const [subCategories, setSubCategories] = useState<SelectOption[]>([]);

  const defaultFilters: ProductFilters = {
    primaryCategory: { value: -1, label: "All Primary Categories" },
    subCategory: { value: -1, label: "All Subcategories" },
    stockStatus: { value: -1, label: "All" },
  };
  const [filters, setFilters] = useState(defaultFilters);
  const [showFilters, setShowFilters] = useState(false);

  const [slideOver, setSlideOver] = useState<SlideOverState>({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title: string, subtitle = "Please try again.") => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getProducts = async function (search: string | null, page: number, filter: ProductFilters) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    const callResult = await makePostApiCall<ProductsResponse, typeof payload>(`products`, payload);
    setLoading(false);
    if (callResult.success) {
      setProducts(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch products");
    }
  };

  const getPrimaryCategoriesForDropdown = async function () {
    const callResult = await makePostApiCall<SelectOption[]>(`/products/categories/dropdown`);
    if (callResult.success) {
      setPrimaryCategories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message ?? "Could not fetch categories");
    }
  };

  const getSubCategoriesForDropdownByPrimary = async function (primaryCategoryId: number) {
    const callResult = await makePostApiCall<SelectOption[]>(`/products/subcategories/dropdown/${primaryCategoryId}`);
    if (callResult.success) {
      setSubCategories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message ?? "Could not fetch subcategories");
    }
  };

  const resetSubCategoryFilter = () => {
    setFilters((prev) => ({
      ...prev,
      subCategory: defaultFilters.subCategory,
    }));
  };

  const resetFilters = () => {
    const filterReset = {
      primaryCategory: defaultFilters.primaryCategory,
      subCategory: defaultFilters.subCategory,
      stockStatus: defaultFilters.stockStatus,
    };
    setFilters(filterReset);
    setShowFilters(false);
    getProducts(null, 1, filterReset);
  };

  const filterComponents = [
    <FilterSingleSelectDropdown
      key="primaryCategory"
      header="Primary Category"
      onChange={(value) =>
        setFilters((prev) => ({
          ...prev,
          primaryCategory: value ?? defaultFilters.primaryCategory,
        }))
      }
      options={primaryCategories}
      resetFilter={() =>
        setFilters((prev) => ({
          ...prev,
          primaryCategory: defaultFilters.primaryCategory,
          subCategory: defaultFilters.subCategory,
        }))
      }
      valueName="primaryCategory"
      value={filters.primaryCategory}
    />,
    <FilterSingleSelectDropdown
      key="subCategory"
      header="Subcategories"
      onChange={(value) =>
        setFilters((prev) => ({
          ...prev,
          subCategory: value ?? defaultFilters.subCategory,
        }))
      }
      options={subCategories}
      resetFilter={() =>
        setFilters((prev) => ({
          ...prev,
          subCategory: defaultFilters.subCategory,
        }))
      }
      valueName="subCategory"
      value={filters.subCategory}
    />,
    <FilterSingleSelectDropdown
      key="stockStatus"
      header="Stock status"
      onChange={(value) =>
        setFilters((prev) => ({
          ...prev,
          stockStatus: value ?? defaultFilters.stockStatus,
        }))
      }
      options={stockFilterStatusOptions}
      resetFilter={() =>
        setFilters((prev) => ({
          ...prev,
          stockStatus: defaultFilters.stockStatus,
        }))
      }
      valueName="stockStatus"
      value={filters.stockStatus}
    />,
  ];

  return {
    emptyModal,
    setEmptyModelOpen,
    getProducts,
    loading,
    products,
    setSlideOver,
    slideOver,
    filterComponents,
    resetFilters,
    showFilters,
    setShowFilters,
    filters,
    getPrimaryCategoriesForDropdown,
    getSubCategoriesForDropdownByPrimary,
    resetSubCategoryFilter,
  };
}
